import type { ResourcePath } from '@occ/contracts';
import { useCan } from '@refinedev/core';
import type { ColumnDef } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { describeHttpError } from '../api/http';
import {
  controlService,
  killProcess,
  listProcesses,
  listServices,
  processLabel,
  type ProcessRow,
  type ServiceAction,
  type ServiceRow,
  type ServiceStatus,
} from '../data/services';
import { bytes, timeAgo } from '../lib/format';
import { ErrorNotice } from './ErrorNotice';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  DataTableColumnHeader,
  EmptyState,
  Field,
  Loading,
  Notice,
  Toasts,
  useToasts,
} from './ui';

/**
 * Servicios y procesos del nodo (Módulo 6.2, RF-10).
 *
 * Las cuatro acciones que cambian algo —`start`, `stop`, `restart` y
 * `processes.kill`— están marcadas como destructivas en el catálogo, así que todas
 * piden confirmación. Se distingue el riesgo: parar el servicio que sostiene el
 * punto de venta de una tienda no es lo mismo que arrancar uno que ya debería estar
 * arriba, y el diálogo lo dice.
 *
 * La tabla de procesos se ordena como la manda el agente —por memoria
 * descendente— porque quien la abre busca al que se está comiendo la caja. La
 * paginación de `DataTable` es lo que evita pintar las 1800 filas que devuelve
 * un Windows de golpe —solo la página vigente llega al DOM—, y el filtro
 * busca sobre la lista completa antes de paginar, así que un proceso de la
 * cola sigue siendo alcanzable por su nombre aunque no esté en la primera
 * página.
 */

const PROCESS_PAGE_SIZE = 50;

const STATUS_LABEL: Record<ServiceStatus, string> = {
  running: 'En marcha',
  stopped: 'Parado',
  failed: 'Fallido',
  unknown: 'Desconocido',
};

const STATUS_TONE: Record<ServiceStatus, 'ok' | 'warn' | 'neutral'> = {
  running: 'ok',
  stopped: 'neutral',
  failed: 'warn',
  unknown: 'neutral',
};

const ACTION_LABEL: Record<ServiceAction, string> = {
  start: 'Arrancar',
  stop: 'Detener',
  restart: 'Reiniciar',
};

/** Lo que el operador tiene que entender antes de pulsar. */
const ACTION_WARNING: Record<ServiceAction, string> = {
  start: 'El servicio pasará a estar en marcha. Es la menos arriesgada de las tres.',
  stop: 'El servicio dejará de dar servicio hasta que alguien lo arranque. Si es el que sostiene la caja, la tienda se para.',
  restart:
    'El servicio se corta y vuelve. Hay un hueco de indisponibilidad, corto pero real, y las conexiones abiertas se pierden.',
};

type PendingControl = { service: ServiceRow; action: ServiceAction };

export function ServicesPanel({
  nodeId,
  alias,
  online,
  resourcePath,
}: {
  nodeId: string;
  alias: string;
  online: boolean;
  resourcePath: ResourcePath;
}) {
  const [services, setServices] = useState<ServiceRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [pending, setPending] = useState<PendingControl | null>(null);
  const [busy, setBusy] = useState(false);

  const [processes, setProcesses] = useState<ProcessRow[]>([]);
  const [processesLoading, setProcessesLoading] = useState(false);
  const [processesError, setProcessesError] = useState<unknown>(null);
  const [filter, setFilter] = useState('');

  const [pid, setPid] = useState('');
  const [confirmingKill, setConfirmingKill] = useState(false);
  const [killError, setKillError] = useState<unknown>(null);
  /**
   * El proceso elegido en la tabla, cuando se llegó al diálogo por ahí. Es lo que
   * permite que la confirmación diga «vas a matar OmniaPOS.exe» y no solo un
   * número: un PID suelto no se puede revisar antes de pulsar.
   */
  const [killTarget, setKillTarget] = useState<ProcessRow | null>(null);

  const toasts = useToasts();

  const { data: canControl } = useCan({
    resource: 'actions',
    action: 'services.control',
    params: { path: resourcePath },
  });
  const { data: canKill } = useCan({
    resource: 'actions',
    action: 'processes.kill',
    params: { path: resourcePath },
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setServices(await listServices(nodeId));
      setError(null);
    } catch (cause) {
      setServices([]);
      setError(cause);
    } finally {
      setLoading(false);
    }
  }, [nodeId]);

  const loadProcesses = useCallback(async () => {
    setProcessesLoading(true);
    try {
      setProcesses(await listProcesses(nodeId));
      setProcessesError(null);
    } catch (cause) {
      setProcesses([]);
      setProcessesError(cause);
    } finally {
      setProcessesLoading(false);
    }
  }, [nodeId]);

  useEffect(() => {
    if (online) void load();
  }, [online, load]);

  useEffect(() => {
    if (online) void loadProcesses();
  }, [online, loadProcesses]);

  /**
   * Filtro sobre la lista completa y no sobre lo que se está pintando: el proceso
   * que se busca casi nunca es de los que más memoria gastan, así que buscar solo
   * entre las filas visibles no encontraría nada. Se busca también por PID porque
   * es el dato que llega por teléfono.
   */
  const matching = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (needle === '') return processes;
    return processes.filter(
      (process) =>
        processLabel(process).toLowerCase().includes(needle) ||
        String(process.pid).includes(needle) ||
        (process.username ?? '').toLowerCase().includes(needle),
    );
  }, [processes, filter]);

  const applyControl = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      const status = await controlService(nodeId, pending.service.name, pending.action);
      toasts.ok(
        `${pending.service.displayName ?? pending.service.name}: ${STATUS_LABEL[status].toLowerCase()}.`,
      );
      setPending(null);
      // Se recarga en vez de fiarse del estado que devuelve la acción: un
      // `restart` puede dejar el servicio arrancando y el estado real llega un
      // instante después.
      await load();
    } catch (cause) {
      toasts.fail(describeHttpError(cause));
    } finally {
      setBusy(false);
    }
  };

  const applyKill = async () => {
    const parsed = Number(pid);
    if (!Number.isInteger(parsed) || parsed <= 0) return;
    setBusy(true);
    setKillError(null);
    try {
      await killProcess(nodeId, parsed);
      const nombre = killTarget ? `${processLabel(killTarget)} (${parsed})` : `Proceso ${parsed}`;
      toasts.ok(`${nombre} terminado en ${alias}.`);
      setPid('');
      setKillTarget(null);
      setConfirmingKill(false);
      // Se vuelve a pedir la lista en vez de quitar la fila a mano: matar un
      // proceso puede arrastrar a sus hijos, y una tabla que enseña procesos
      // muertos es peor que una que tarda un segundo más.
      await loadProcesses();
    } catch (cause) {
      setConfirmingKill(false);
      setKillError(cause);
    } finally {
      setBusy(false);
    }
  };

  const askKill = (process: ProcessRow) => {
    setKillTarget(process);
    setPid(String(process.pid));
    setConfirmingKill(true);
  };

  const controlDisabled = !online || !canControl?.can;

  const serviceColumns = useMemo<ColumnDef<ServiceRow>[]>(
    () => [
      {
        id: 'servicio',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Servicio" />,
        accessorFn: (service) => service.displayName ?? service.name,
        meta: { label: 'Servicio' },
        cell: ({ row }) => (
          <>
            <div>{row.original.displayName ?? row.original.name}</div>
            {row.original.displayName && (
              <div className="font-mono text-[11px] opacity-50">{row.original.name}</div>
            )}
          </>
        ),
      },
      {
        id: 'estado',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Estado" />,
        accessorFn: (service) => service.status,
        meta: { label: 'Estado' },
        cell: ({ row }) => (
          <Badge tone={STATUS_TONE[row.original.status]}>
            {STATUS_LABEL[row.original.status]}
          </Badge>
        ),
      },
      {
        id: 'arranque',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Arranque" />,
        accessorFn: (service) => service.startType ?? '—',
        meta: { label: 'Arranque' },
        cell: ({ getValue }) => <span className="opacity-70">{getValue<string>()}</span>,
      },
      {
        id: 'acciones',
        header: 'Acciones',
        meta: { label: 'Acciones' },
        enableSorting: false,
        cell: ({ row }) => {
          const service = row.original;
          return (
            <div className="flex justify-end gap-1">
              {(['start', 'stop', 'restart'] as ServiceAction[]).map((action) => (
                <Button
                  key={action}
                  variant={action === 'start' ? 'secondary' : 'danger'}
                  disabled={
                    controlDisabled ||
                    // Arrancar lo que ya está en marcha y detener lo que ya
                    // está parado son órdenes sin efecto que solo ensucian la
                    // bitácora.
                    (action === 'start' && service.status === 'running') ||
                    (action === 'stop' && service.status === 'stopped')
                  }
                  title={canControl?.can ? ACTION_WARNING[action] : canControl?.reason}
                  onClick={() => setPending({ service, action })}
                >
                  {ACTION_LABEL[action]}
                </Button>
              ))}
            </div>
          );
        },
      },
    ],
    [controlDisabled, canControl],
  );

  const processColumns = useMemo<ColumnDef<ProcessRow>[]>(
    () => [
      {
        id: 'proceso',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Proceso" />,
        accessorFn: (process) => processLabel(process),
        meta: { label: 'Proceso' },
        cell: ({ row }) => {
          const process = row.original;
          return (
            <>
              <div className={process.name.trim() === '' ? 'opacity-60' : undefined}>
                {processLabel(process)}
              </div>
              {process.ppid !== undefined && process.ppid > 0 && (
                <div className="font-mono text-[11px] opacity-50">padre {process.ppid}</div>
              )}
            </>
          );
        },
      },
      {
        id: 'pid',
        header: ({ column }) => <DataTableColumnHeader column={column} title="PID" />,
        accessorFn: (process) => process.pid,
        meta: { label: 'PID' },
        cell: ({ getValue }) => <span className="font-mono">{getValue<number>()}</span>,
      },
      {
        id: 'usuario',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Usuario" />,
        accessorFn: (process) => process.username ?? '—',
        meta: { label: 'Usuario' },
        cell: ({ getValue }) => <span className="opacity-70">{getValue<string>()}</span>,
      },
      {
        id: 'memoria',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Memoria" />,
        accessorFn: (process) => process.memoryBytes ?? -1,
        meta: { label: 'Memoria' },
        cell: ({ row }) => (
          <span className="text-right">
            {row.original.memoryBytes === undefined ? '—' : bytes(row.original.memoryBytes)}
          </span>
        ),
      },
      {
        id: 'cpu',
        header: ({ column }) => <DataTableColumnHeader column={column} title="CPU" />,
        // Acumulado desde que arrancó el proceso, no del último segundo: se
        // dice en el encabezado de la tarjeta y no por fila, pero por eso
        // puede pasar de 100.
        accessorFn: (process) => process.cpuPercent ?? -1,
        meta: { label: 'CPU' },
        cell: ({ row }) => (
          <span className="text-right opacity-70">
            {row.original.cpuPercent === undefined ? '—' : `${row.original.cpuPercent.toFixed(1)} %`}
          </span>
        ),
      },
      {
        id: 'arrancado',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Arrancado" />,
        accessorFn: (process) => process.startedAt?.getTime() ?? 0,
        meta: { label: 'Arrancado' },
        cell: ({ row }) => (
          <span className="opacity-70">
            {row.original.startedAt ? timeAgo(row.original.startedAt) : '—'}
          </span>
        ),
      },
      {
        id: 'acciones',
        header: 'Acciones',
        meta: { label: 'Acciones' },
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <Button
              variant="danger"
              disabled={
                !online ||
                !canKill?.can ||
                // El pid 0 sale en la lista de todo Windows y no se puede
                // matar: el contrato lo rechaza y el agente también.
                row.original.pid <= 0
              }
              title={canKill?.can ? undefined : canKill?.reason}
              onClick={() => askKill(row.original)}
            >
              Terminar
            </Button>
          </div>
        ),
      },
    ],
    [online, canKill],
  );

  return (
    <div className="grid gap-4">
      <Toasts toasts={toasts.toasts} />

      <Card
        title="Servicios"
        actions={
          <Button variant="ghost" disabled={!online || loading} onClick={() => void load()}>
            Recargar
          </Button>
        }
      >
        <div className="grid gap-3">
          {!online && (
            <Notice tone="warn">
              {alias} no está conectado: la lista de servicios se pide al agente en el momento.
            </Notice>
          )}

          {error !== null && (
            <ErrorNotice
              error={error}
              what="Listar los servicios"
              pendingHint="Necesita la capacidad «services» en el agente de este nodo."
            />
          )}

          {loading && services.length === 0 ? (
            <Loading label="Pidiendo los servicios al nodo…" />
          ) : services.length === 0 ? (
            <EmptyState title={error !== null ? 'Sin lista' : 'El nodo no reportó servicios'} />
          ) : (
            <DataTable
              columns={serviceColumns}
              data={services}
              emptyMessage="El nodo no reportó servicios."
            />
          )}
        </div>
      </Card>

      <Card
        title="Procesos"
        actions={
          <Button
            variant="ghost"
            disabled={!online || processesLoading}
            onClick={() => void loadProcesses()}
          >
            Recargar
          </Button>
        }
      >
        <div className="grid gap-3">
          {!online && (
            <Notice tone="warn">
              {alias} no está conectado: la lista de procesos se pide al agente en el momento.
            </Notice>
          )}

          {processesError !== null && (
            <ErrorNotice
              error={processesError}
              what="Listar los procesos"
              pendingHint="Necesita la capacidad «processes» en el agente de este nodo."
            />
          )}

          {killError !== null && (
            <ErrorNotice
              error={killError}
              what="Terminar el proceso"
              pendingHint="Necesita la capacidad «processes» en el agente de este nodo."
            />
          )}

          {processesLoading && processes.length === 0 ? (
            <Loading label="Pidiendo los procesos al nodo…" />
          ) : processes.length === 0 ? (
            <EmptyState
              title={processesError !== null ? 'Sin lista' : 'El nodo no reportó procesos'}
            />
          ) : (
            <>
              <Field
                label="Filtrar"
                value={filter}
                placeholder="nombre, PID o usuario"
                hint={`${processes.length} procesos, ordenados por memoria. El nodo los manda todos y el filtro busca sobre la lista entera.`}
                onChange={(event) => setFilter(event.target.value)}
              />

              {matching.length === 0 ? (
                <EmptyState title={`Ningún proceso coincide con «${filter.trim()}»`} />
              ) : (
                // La `key` en el filtro y no en `processes` a propósito: pulsar
                // «Recargar» reemplaza `processes` entero y no debe tirar al
                // operador de la página en la que estaba (la razón de que
                // `DataTable` ya no se resetee sola); escribir un filtro nuevo
                // sí es una consulta distinta y sí debe volver a la página 1.
                <DataTable
                  key={filter}
                  columns={processColumns}
                  data={matching}
                  pageSize={PROCESS_PAGE_SIZE}
                  emptyMessage={`Ningún proceso coincide con «${filter.trim()}».`}
                />
              )}
            </>
          )}

          {/*
            El formulario por PID se queda aunque ya haya tabla: el número llega por
            teléfono desde el gestor de tareas del nodo, y obligar a buscarlo entre
            1800 filas para poder pulsar un botón sería un paso de más.
          */}
          <form
            className="flex flex-wrap items-end gap-3 border-t border-black/5 pt-3 dark:border-white/10"
            onSubmit={(event) => {
              event.preventDefault();
              setKillTarget(processes.find((process) => process.pid === Number(pid)) ?? null);
              setConfirmingKill(true);
            }}
          >
            <Field
              label="PID"
              type="number"
              min={1}
              value={pid}
              placeholder="4812"
              hint="El identificador de proceso, tal como lo da el gestor de tareas del nodo."
              onChange={(event) => setPid(event.target.value)}
            />
            <Button
              type="submit"
              variant="danger"
              disabled={!online || !canKill?.can || Number(pid) <= 0}
              title={canKill?.can ? undefined : canKill?.reason}
            >
              Terminar proceso
            </Button>
          </form>
        </div>
      </Card>

      <ConfirmDialog
        open={pending !== null}
        title={
          pending
            ? `${ACTION_LABEL[pending.action]} ${pending.service.displayName ?? pending.service.name}`
            : ''
        }
        confirmLabel={pending ? ACTION_LABEL[pending.action] : 'Confirmar'}
        danger={pending?.action !== 'start'}
        busy={busy}
        onCancel={() => setPending(null)}
        onConfirm={() => void applyControl()}
      >
        <p>
          En <strong>{alias}</strong>, servicio <code>{pending?.service.name}</code>.
        </p>
        {pending && <Notice tone="warn">{ACTION_WARNING[pending.action]}</Notice>}
      </ConfirmDialog>

      <ConfirmDialog
        open={confirmingKill}
        title={
          killTarget
            ? `Terminar ${processLabel(killTarget)} (${pid})`
            : `Terminar el proceso ${pid}`
        }
        confirmLabel="Terminar"
        busy={busy}
        onCancel={() => setConfirmingKill(false)}
        onConfirm={() => void applyKill()}
      >
        <p>
          Se va a terminar{' '}
          {killTarget ? (
            <>
              <strong>{processLabel(killTarget)}</strong>, PID <strong>{pid}</strong>
              {killTarget.username ? (
                <>
                  , de <strong>{killTarget.username}</strong>
                </>
              ) : null}
            </>
          ) : (
            <>
              el proceso <strong>{pid}</strong>
            </>
          )}{' '}
          en <strong>{alias}</strong>.
        </p>
        <Notice tone="warn">
          Se mata sin avisar al programa: lo que tuviera sin guardar se pierde. Un PID equivocado
          puede tumbar el propio agente y dejar el nodo sin acceso remoto hasta que alguien lo
          reinicie a mano.
        </Notice>
      </ConfirmDialog>
    </div>
  );
}
